import type { BodyBlock, Group, GroupKind, Statute, Unit } from '../types';

/**
 * Legacy criminal adapter (Pakistan Penal Code 1860, Code of Criminal Procedure 1898).
 *
 * The legacy shape is fundamentally different from v2:
 *   {
 *     title: string,
 *     parts?: [{ title, chapters: [{ title, sections: [{ section, title, content }] }] }],
 *     chapters?: [...] // when no parts
 *   }
 *
 * Sections are flat: just `section`, `title`, `content` (string). No
 * subsections / clauses / provisos. Some sections are "[Repealed]" or
 * "[Omitted]" placeholders.
 *
 * We synthesize stable ids based on the section number (`s-{n}`) so anchors
 * line up with the existing navigator id patterns.
 */

type AnyRecord = Record<string, unknown>;

const isObject = (v: unknown): v is AnyRecord =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

const asString = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const trimOrEmpty = (v: unknown): string => asString(v).trim();

function devWarn(message: string, ...rest: unknown[]) {
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`[legacyCriminalAdapter] ${message}`, ...rest);
  }
}

function splitChapterTitle(t: string): { label: string; title?: string } {
  const txt = trimOrEmpty(t);
  if (!txt) return { label: 'Chapter' };
  const m = txt.match(/^(Chapter\s+[A-Za-z0-9IVXLCDM-]+)\s+(.*)$/i);
  if (m) return { label: m[1], title: m[2].trim() || undefined };
  return { label: txt };
}

function splitPartTitle(t: string): { label: string; title?: string } {
  const txt = trimOrEmpty(t);
  if (!txt) return { label: 'Part' };
  const m = txt.match(/^(Part\s+[A-Za-z0-9IVXLCDM-]+)\s+(.*)$/i);
  if (m) return { label: m[1], title: m[2].trim() || undefined };
  return { label: txt };
}

function buildSectionUnit(
  raw: AnyRecord,
  pathPrefix: string,
  fallbackOrder: number,
): Unit | null {
  const number = trimOrEmpty(raw.section) || trimOrEmpty(raw.sectionNo) || trimOrEmpty(raw.label);
  const titleField = trimOrEmpty(raw.title);
  if (!number && !titleField) return null;

  const id = number ? `s-${number}` : `${pathPrefix}-s-${fallbackOrder}`;
  const numberValue = number || String(fallbackOrder);
  const numberDisplay = number ? `Section ${number}` : `Section ${numberValue}`;

  const content = trimOrEmpty(raw.content);
  const body: BodyBlock[] = [];
  if (content) body.push({ kind: 'paragraph', text: content });

  // Optional richer fields if a future legacy file ever carries them.
  const illustrations = Array.isArray(raw.illustrations) ? raw.illustrations : [];
  illustrations.forEach((il) => {
    const text = typeof il === 'string' ? il.trim() : trimOrEmpty((il as AnyRecord)?.text);
    if (text) body.push({ kind: 'illustration', text });
  });
  const exceptions = Array.isArray(raw.exceptions) ? raw.exceptions : [];
  exceptions.forEach((ex) => {
    const text = typeof ex === 'string' ? ex.trim() : trimOrEmpty((ex as AnyRecord)?.text);
    if (text) body.push({ kind: 'explanation', text: `Exception: ${text}` });
  });

  return {
    id,
    kind: 'section',
    number: numberValue,
    numberDisplay,
    title: titleField || `Section ${numberValue}`,
    body,
  };
}

function buildChapterGroup(
  chapter: AnyRecord,
  ancestors: string[],
  groupSeq: { value: number },
  unitSeq: { value: number },
): Group | null {
  const headingRaw = trimOrEmpty(chapter.title);
  const { label, title } = splitChapterTitle(headingRaw);
  const sections = Array.isArray(chapter.sections) ? (chapter.sections as AnyRecord[]) : [];
  if (sections.length === 0) return null;

  const units: Unit[] = [];
  for (const sec of sections) {
    if (!isObject(sec)) continue;
    const u = buildSectionUnit(sec, `chap-${groupSeq.value}`, unitSeq.value);
    if (u) {
      units.push(u);
      unitSeq.value += 1;
    }
  }
  if (units.length === 0) return null;

  const id = `chap-${groupSeq.value++}`;
  return {
    id,
    kind: 'chapter' as GroupKind,
    label: label || 'Chapter',
    ...(title ? { title } : {}),
    ...(ancestors.length ? { ancestors: ancestors.slice() } : {}),
    units,
  };
}

function buildStatute(raw: AnyRecord, hint?: string): Statute {
  const title = trimOrEmpty(raw.title) || 'Untitled Statute';
  const shortCode = trimOrEmpty(raw.shortTitle) || title;
  const yearMatch = title.match(/(\d{4})/);
  const year = yearMatch ? parseInt(yearMatch[1], 10) : 0;
  const jurisdiction = 'PK';
  const category = hint || 'criminal';

  const groups: Group[] = [];
  const groupSeq = { value: 0 };
  const unitSeq = { value: 0 };

  const parts = Array.isArray(raw.parts) ? (raw.parts as AnyRecord[]) : null;
  const topChapters = Array.isArray(raw.chapters) ? (raw.chapters as AnyRecord[]) : null;

  if (parts && parts.length) {
    for (const part of parts) {
      if (!isObject(part)) continue;
      const headingRaw = trimOrEmpty(part.title);
      const { label: partLabel, title: partTitle } = splitPartTitle(headingRaw);
      const partAncestor = [partLabel, partTitle].filter(Boolean).join(' ');
      const chapters = Array.isArray(part.chapters) ? (part.chapters as AnyRecord[]) : [];
      for (const chapter of chapters) {
        if (!isObject(chapter)) continue;
        const grp = buildChapterGroup(chapter, partAncestor ? [partAncestor] : [], groupSeq, unitSeq);
        if (grp) groups.push(grp);
      }
    }
  } else if (topChapters && topChapters.length) {
    for (const chapter of topChapters) {
      if (!isObject(chapter)) continue;
      const grp = buildChapterGroup(chapter, [], groupSeq, unitSeq);
      if (grp) groups.push(grp);
    }
  } else {
    devWarn('No parts or chapters found at top level', Object.keys(raw));
  }

  const idHint = hint || trimOrEmpty(raw.shortTitle) || title;
  const id = idHint.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'statute';

  return {
    id,
    title,
    shortCode,
    year,
    jurisdiction,
    category,
    groups,
    metadata: { ...raw },
  };
}

export function adaptStatute(rawJson: unknown, hint?: string): Statute {
  if (!isObject(rawJson)) {
    devWarn('adaptStatute received non-object input', rawJson);
    return {
      id: 'unknown',
      title: '',
      shortCode: '',
      year: 0,
      jurisdiction: 'PK',
      category: hint || 'criminal',
      groups: [],
      metadata: {},
    };
  }
  return buildStatute(rawJson, hint);
}
